'use client';

import { useMemo, type ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import {
  getCvssVersionFromVector,
  isCvssBaseVectorChanged,
  requiresCvssVectorExplanation,
  resolveCvssMetricReasonDrafts,
  type CvssMetricReason,
} from '@terra/shared-cvss';
import { Field } from '../../components/field';
import { Textarea } from '../../components/textarea';
import { BodyDefault, HeadingItem } from '../../components/typography';

type CvssWithReasons = {
  vector: string;
  metricReasons?: CvssMetricReason[] | null;
  explanation?: string | null;
};

export type CvssMetricReasonsFieldsProps = {
  originalCvss: CvssWithReasons | null | undefined;
  cvss: CvssWithReasons | null | undefined;
  onReasonsChange: (next: {
    metricReasons: CvssMetricReason[];
    explanation: string;
  }) => void;
};

export function CvssMetricReasonsFields({
  originalCvss,
  cvss,
  onReasonsChange,
}: CvssMetricReasonsFieldsProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.sections.cvssAssessment.reasons',
  );
  const drafts = useMemo(
    () =>
      cvss?.vector
        ? resolveCvssMetricReasonDrafts({
            previousVector: originalCvss?.vector,
            previousReasons: originalCvss?.metricReasons ?? [],
            nextVector: cvss.vector,
            draftReasons: cvss.metricReasons ?? [],
          })
        : [],
    [cvss, originalCvss],
  );

  if (
    !cvss?.vector ||
    !isCvssBaseVectorChanged({
      previous: originalCvss?.vector,
      next: cvss.vector,
    })
  ) {
    return null;
  }

  const explanation = cvss.explanation ?? '';
  const showVectorExplanation = requiresCvssVectorExplanation({
    previousVector: originalCvss?.vector,
    nextVector: cvss.vector,
  });
  const explanationMissing =
    showVectorExplanation && explanation.trim() === '';
  const explanationVersion = getCvssVersionFromVector(cvss.vector);
  const emit = ({
    metric,
    reason,
    nextExplanation,
  }: {
    metric?: string;
    reason?: string;
    nextExplanation?: string;
  }) =>
    onReasonsChange({
      metricReasons: drafts.flatMap(
        ({ carried: _carried, ...metricReason }) => {
          const nextReason =
            metricReason.metric === metric
              ? {
                  ...metricReason,
                  reason: reason ?? '',
                  source: 'USER' as const,
                }
              : metricReason;

          return nextReason.reason.trim() === '' ? [] : [nextReason];
        },
      ),
      explanation: nextExplanation ?? explanation,
    });

  return (
    <VStack gap={3} alignItems="stretch" w="full">
      <HeadingItem>{t('title')}</HeadingItem>
      <BodyDefault color="fg.muted">{t('description')}</BodyDefault>
      {drafts
        .filter((draft) => !draft.carried)
        .map((draft) => (
          <Field.Root key={draft.metric} required>
            <Field.Label>
              {t('metricLabel', { metric: draft.metric, value: draft.value })}
            </Field.Label>
            <Field.Context>
              {(field) => (
                <Textarea
                  id={field.ids.control}
                  size="sm"
                  rows={2}
                  value={draft.reason}
                  placeholder={t('placeholder')}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                    emit({ metric: draft.metric, reason: event.target.value })
                  }
                />
              )}
            </Field.Context>
          </Field.Root>
        ))}
      {showVectorExplanation ? (
        <Field.Root required invalid={explanationMissing}>
          <Field.Label>{t('explanationLabel')}</Field.Label>
          <Field.Context>
            {(field) => (
              <Textarea
                id={field.ids.control}
                size="sm"
                rows={3}
                value={explanation}
                placeholder={t('explanationPlaceholder')}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  emit({ nextExplanation: event.target.value })
                }
              />
            )}
          </Field.Context>
          {explanationMissing && explanationVersion ? (
            <Field.ErrorText>
              {t('explanationMissing', { version: explanationVersion })}
            </Field.ErrorText>
          ) : null}
        </Field.Root>
      ) : null}
    </VStack>
  );
}
